import * as React from 'react';
import i18n from 'i18next';
import _set from 'lodash/set';
import { action } from 'mobx';

// types
import { Widget } from '@sprinklr/stories/widget/Widget';
import { Theme } from 'models/Theme/Theme';
import { DataTableOptions } from 'components/_charts/DataTable/types';
import { AnalyticsProjection } from '@sprinklr/stories/analytics/AnalyticsRequest';

// components
import { CollapseProps, TunablesCollapse } from 'components/Sidebar/RenderTunables/RenderTunables';
import ActionCheckboxGroup from '../../components/_UI/Forms/Actions/ActionCheckboxGroup';
import ActionSliderGroup from 'components/_UI/Forms/Actions/ActionSliderGroup';
import ActionSelectGroup from 'components/_UI/Forms/Actions/ActionSelectGroup';
import ActionSegmentedGroup from 'components/_UI/Forms/Actions/ActionSegmentedGroup';
import ActionInput from 'components/_UI/Forms/Actions/ActionInput';
import LabelGroup from 'components/_UI/Forms/LabelGroup/LabelGroup';
import ActionCheckboxSliderColorSwatchGroup from 'components/_UI/Forms/Actions/ActionCheckboxSliderColorSwatchGroup';
import { Flexbox, Input, LabeledCheckbox, TooltipPopper } from 'components/spaceKit';
import { StyledCollapse } from '../ComboChartWidget/styled-components';

// constants
import {
    endianOptions,
    horizontalAlignOptions,
} from 'components/Panel/PanelEditorSidebar/PanelDesign/PanelDesignOptions';
import { BASE_BORDER_COLOR, getPrettyMetricLabel } from './helpers';
import { METRIC_VALUE_FORMAT_VS_NUMBER_FORMAT } from '../RankedTableWidget/constants';

// helpers
import {
    headerTunables,
    opacityTunables,
    smartSizeTunables,
    themeTunables,
} from 'components/Sidebar/RenderTunables/SharedTunables';
import { getMetricDesignOptionsKey } from '../RankedTableWidget/helpers';
import { updateWidgetOptions } from '../../utils/Widget/WidgetUtils';
import { NumberFormat } from '@sprinklr/display-builder/widgets/DataTableWidget/types';

const getFallbackColor = (options, mergedTheme: Theme): string => {
    return options?.rows?.borders?.base?.color ?? mergedTheme?.typography?.color ?? '';
};

function getMetricOptionsMap(widget: Widget): (metric: AnalyticsProjection) => NumberFormat {
    const options = widget.options?.metricDesignOptions;
    const {
        usePretty: fallbackPretty,
        decimals: fallbackDecimals,
    } = widget.options.base.numberFormat;
    return function(metric: AnalyticsProjection): NumberFormat {
        return {
            usePretty:
                options?.[
                    getMetricDesignOptionsKey(metric.measurementName, metric.aggregateFunction)
                ]?.numberFormat?.usePretty ?? fallbackPretty,
            decimals:
                options?.[
                    getMetricDesignOptionsKey(metric.measurementName, metric.aggregateFunction)
                ]?.numberFormat?.decimals ?? fallbackDecimals,
        };
    };
}

function handleUsePrettyChange(
    checked: boolean,
    widget: Widget,
    metric: AnalyticsProjection,
    fallbackDecimals: number
) {
    const numberFormat = {
        decimals:
            widget.options[
                `metricDesignOptions.${getMetricDesignOptionsKey(
                    metric.measurementName,
                    metric.aggregateFunction
                )}.numberFormat`
            ]?.decimals ?? fallbackDecimals,
        usePretty: checked,
    };

    updateWidgetOptions(
        widget,
        `metricDesignOptions.${getMetricDesignOptionsKey(
            metric.measurementName,
            metric.aggregateFunction
        )}.numberFormat`,
        numberFormat
    );
}

function handleDecimalsChange(
    value: string,
    widget: Widget,
    metric: AnalyticsProjection,
    fallbackUsePretty: boolean
) {
    const eventValue = parseInt(value, 10);

    const numberFormat = {
        usePretty:
            widget.options[
                `metricDesignOptions.${getMetricDesignOptionsKey(
                    metric.measurementName,
                    metric.aggregateFunction
                )}.numberFormat`
            ]?.usePretty ?? fallbackUsePretty,
        decimals: eventValue,
    };

    updateWidgetOptions(
        widget,
        `metricDesignOptions.${getMetricDesignOptionsKey(
            metric.measurementName,
            metric.aggregateFunction
        )}.numberFormat`,
        numberFormat
    );
}

export function metricDesignTunables(
    widget: Widget,
    getMetricOptionsMapFn: (
        widget: Widget
    ) => (metric: AnalyticsProjection) => NumberFormat = getMetricOptionsMap
) {
    const metricOptionsMap = getMetricOptionsMapFn(widget);

    return (
        <Flexbox gap='l' vertical key='metricLayerTunables'>
            {widget.analyticsRequests?.[0]?.projections?.map((metric, index) => {
                const { usePretty, decimals } = metricOptionsMap(metric);
                const label = getPrettyMetricLabel(metric);
                return (
                    <StyledCollapse
                        label={
                            <TooltipPopper label={label}>
                                {
                                    i18n.t(
                                        'These options will override the column/row Metric Value Format options for this metric'
                                    ) as string
                                }
                            </TooltipPopper>
                        }
                        key={`${label}-${index}`}>
                        <LabeledCheckbox
                            label={i18n.t('Shortened Number (K,M,B)')}
                            checked={usePretty}
                            data-checked={usePretty}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                handleUsePrettyChange(
                                    event.target.checked,
                                    widget,
                                    metric,
                                    decimals
                                )
                            }
                        />
                        <LabelGroup key='widget.options.base.decimals' label={i18n.t('Decimals')}>
                            <Input
                                type='number'
                                data-id='numberFormat-decimals'
                                data-value={decimals}
                                value={decimals}
                                min={0}
                                max={10}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                    handleDecimalsChange(
                                        event.target.value,
                                        widget,
                                        metric,
                                        usePretty
                                    )
                                }
                            />
                        </LabelGroup>
                    </StyledCollapse>
                );
            })}
        </Flexbox>
    );
}

const DataTableTunables = (widget: Widget, mergedTheme: Theme): TunablesCollapse[] => {
    const options = widget.options as DataTableOptions;

    const showClearBorder = options.rows.borders.base.color !== BASE_BORDER_COLOR;
    return [
        {
            props: {
                label: i18n.t('Table'),
                startOpen: true,
            } as CollapseProps,
            children: [
                ...headerTunables(widget),
                opacityTunables(widget),
                <ActionCheckboxGroup
                    key='widget.options.autoRowHeight'
                    label={i18n.t('Auto Distribute Row Height')}
                    option={options.layout}
                    value='autoRowHeight'
                />,
                <ActionSliderGroup
                    key='widget.options.layout.padding'
                    label={i18n.t('Cell Padding')}
                    option={options.layout}
                    value='padding'
                    showValue
                    min={0}
                    max={100}
                />,
                <ActionSliderGroup
                    key='widget.options.default.size'
                    label={i18n.t('Font Size')}
                    option={options.base}
                    value='size'
                    showValue
                    min={0}
                    max={100}
                />,
                <ActionSegmentedGroup
                    key='widget.options.alignment'
                    label={i18n.t('Alignment')}
                    option={options.base}
                    value={'alignment'}
                    options={horizontalAlignOptions}
                />,
                smartSizeTunables(widget),
            ],
        },
        widget.type === 'dataTableCrossTab' && {
            props: {
                label: i18n.t('Columns'),
                startOpen: true,
            } as CollapseProps,
            children: [metricDesignTunables(widget)],
        },
        {
            props: {
                label: i18n.t('Formatting'),
                startOpen: true,
            } as CollapseProps,
            children: [
                <ActionCheckboxGroup
                    key='widget.options.base.usePretty'
                    label={i18n.t('Shortened Number (K,M,B)')}
                    option={options.base.numberFormat}
                    value='usePretty'
                />,
                <LabelGroup key='widget.options.base.decimals' label={i18n.t('Decimals')}>
                    <ActionInput
                        min={0}
                        max={10}
                        type='number'
                        option={options.base.numberFormat}
                        value='decimals'
                    />
                </LabelGroup>,

                <ActionSelectGroup
                    label={i18n.t('Date Format')}
                    key='widget.options.base.timeFormat'
                    option={options.base}
                    value='timeFormat'
                    options={endianOptions}
                />,
            ],
        },
        {
            props: {
                label: i18n.t('Borders'),
                startOpen: true,
            } as CollapseProps,
            children: [
                <ActionCheckboxSliderColorSwatchGroup
                    key='widget.options.border'
                    label={i18n.t('Row Borders')}
                    showValue
                    checkbox={{
                        checked: options.rows.borders.base.enabled,
                        value: 'enabled',
                        option: options.rows.borders.base,
                    }}
                    slider={{
                        min: 10,
                        max: 100,
                        value: 'size',
                        option: options.rows.borders.base,
                    }}
                    colorSwatch={{
                        option: options.rows.borders.base,
                        value: 'color',
                        onClear: showClearBorder
                            ? action(() => (options.rows.borders.base.color = BASE_BORDER_COLOR))
                            : undefined,
                        secondaryIcon: showClearBorder ? 'icon-undo' : undefined,
                        color:
                            options.rows.borders.base.color ||
                            getFallbackColor(options, mergedTheme),
                        onChange: action(
                            ({ rgb: { r, g, b, a } }) =>
                                (options.rows.borders.base.color = `rgba(${r}, ${g}, ${b}, ${a})`)
                        ),
                    }}
                />,
                <ActionCheckboxSliderColorSwatchGroup
                    key='widget.options.top.borders'
                    label={i18n.t('Top Border')}
                    showValue
                    checkbox={{
                        checked: options.rows.borders.top.enabled,
                        value: 'enabled',
                        option: options.rows.borders.top,
                    }}
                    slider={{
                        min: 10,
                        max: 100,
                        value: 'size',
                        option: options.rows.borders.top,
                    }}
                    colorSwatch={{
                        option: options.rows.borders.top,
                        value: 'color',
                        onClear: action(() => (options.rows.borders.top.color = '')),
                        secondaryIcon: options.rows.borders.top.color ? 'icon-undo' : undefined,
                        color:
                            options.rows.borders.top.color ||
                            getFallbackColor(options, mergedTheme),
                        onChange: action(
                            ({ rgb: { r, g, b, a } }) =>
                                (options.rows.borders.top.color = `rgba(${r}, ${g}, ${b}, ${a})`)
                        ),
                    }}
                />,
                <ActionCheckboxSliderColorSwatchGroup
                    key='widget.options.bottom.borders'
                    label={i18n.t('Bottom Border')}
                    showValue
                    checkbox={{
                        checked: options.rows.borders.bottom.enabled,
                        value: 'enabled',
                        option: options.rows.borders.bottom,
                    }}
                    slider={{
                        min: 10,
                        max: 100,
                        value: 'size',
                        option: options.rows.borders.bottom,
                    }}
                    colorSwatch={{
                        option: options.rows.borders.bottom,
                        value: 'color',
                        onClear: action(() => (options.rows.borders.bottom.color = '')),
                        secondaryIcon: options.rows.borders.bottom.color ? 'icon-undo' : undefined,
                        color:
                            options.rows.borders.bottom.color ||
                            getFallbackColor(options, mergedTheme),
                        onChange: action(
                            ({ rgb: { r, g, b, a } }) =>
                                (options.rows.borders.bottom.color = `rgba(${r}, ${g}, ${b}, ${a})`)
                        ),
                    }}
                />,
            ],
        },
        themeTunables(widget, { color: true, image: true, email: true, imageSize: true }),
    ];
};

export default DataTableTunables;
